// BRIEF 105 §3b — NOTES LEAVE THE ROW AND GET THEIR OWN DRAWER.
//
// Laurent's reading: "notes are not visible or truncated when Description is
// big, and in addition one has to click on the lock and open the edit modal
// just to see the note."
//
// Two different faults, on two different shapes, and this cures both:
//
//   · ON A TASK ROW the note is a flex item on the title line
//     (`CaseTaskRow.tsx`, `Dashboard.tsx`) with `text-overflow: ellipsis` — so
//     a long title genuinely eats it, and all that is left is a `title=`
//     tooltip.
//   · ON AN EVENT CARD it is not truncated at all: `.event-meta` is
//     `flex-wrap: wrap` (App.css:837) and clips nothing. What is wrong there is
//     that the note is one unlabelled span among the time, the location, the
//     case pill and the document link — and that changing it means opening the
//     modal.
//
// So the drawer is not only a width fix. It is the second half of the sentence:
// the note becomes EDITABLE where it is read.
//
// ── §3b-ii — THE SAVE PATH IS THE HOST'S, AND THAT IS DELIBERATE ────────────
//
// "an editable field in a drawer needs a save path, and that is where this goes
//  wrong quietly. The existing drawers display and toggle; this one writes. Use
//  the same write path the modal uses for notes — do not invent a second one,
//  and do not leave a field that looks editable and discards on blur."
//
// This component owns NO write path. `onSave` is required, and each host passes
// the one it already uses for every other field of the same row:
//
//   CaseTaskRow      `commitField({ notes })` → `svTkData`  (the tk modal's own
//                    save, extracted in Phase 29 — CLAUDE.md, CRUD pattern)
//   CaseEventRow     `patchEvent({ notes })`  → `s(SK.ag, …, sAg)`, which is
//                    the same engine and the same key that
//                    `saveAgendaEvent` (App.tsx:7444) writes through
//   Dashboard task   `svTkData({ ...t, notes })` — the same function, as a prop
//
// AND IT SAVES ON BLUR. A field that looks editable and discards is the fault
// §3b-ii names by name; blur-commit is also the vocabulary the inline title and
// date editors on these same rows already use, so nothing new is being taught.
// Escape reverts, Ctrl/Cmd+Enter commits and leaves. An empty field saves
// `null` rather than `""`, because every consumer of `notes` tests it for
// truthiness — including the toggle that decides whether this drawer's icon is
// lit — and `""` would light it.
import { useEffect, useRef, useState } from "react";
import { NO_AUTOFILL, noAutofillClass } from "../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";

/* BRIEF 107 §2b — `notesToggleVisible` AND `NotesToggle` MOVED to
   `RowDrawerToggle.tsx`, unchanged. 105 put them here because the notes icon
   was the only one of its kind; §3 gives the documents icon the same two states
   and the same chrome, and a DOCUMENTS toggle living in `NotesDrawer.tsx` would
   be worse than either home. The drawer stays; the control went to the file
   named for controls. Nothing about their behaviour moved with them. */

/**
 * BRIEF 118 §9 — "DOES THIS ROW HOLD A NOTE", ONCE.
 *
 * `RowDrawerToggle`'s header says the predicate stays with each caller because
 * "a note has something when its text is non-empty" — a sentence, in three
 * files, spelled `!!row.notes` at every one of them. THE DRAWER DOES NOT SPELL
 * IT THAT WAY: it renders `initial.trim()` (:133), so a note of blanks displays
 * "No notes." while the icon beside it lights.
 *
 * MEASURED on watt: the two surfaces agree exactly (Dashboard and CaseView both
 * render `--tm` for `notes: null` and `--tp` for a note), so the report is not
 * about a host — it is about the two tests disagreeing on one value. Whitespace
 * reaches the column by real paths: the meeting-create route stores
 * `typeof b.notes === 'string' ? b.notes : ''` untrimmed and then `notes ||
 * null` (routes/agenda.js:852, :959), which passes "   " straight through, and
 * `notes` is a plain ALLOWED field on the ordinary event PUT (:162).
 *
 * So the trim rule lives with the thing that owns it — this drawer — and the
 * icons ask it rather than each other.
 */
export function hasNoteText(value: unknown): boolean {
  return typeof value === "string" && value.trim() !== "";
}

export default function NotesDrawer({
  value,
  canWrite,
  onSave,
  hideTitle,
}: {
  value?: string | null;
  canWrite?: boolean;
  onSave: (next: string | null) => void;
  /* 111 §4a — the stage row is the exception to 110 §1f's "every mount is a
     drawer": its drawer sits directly under a row that already names itself,
     and the heading crowded it. Passed ONLY by CaseStageTimeline; the
     task/event/Dashboard mounts keep the heading. */
  hideTitle?: boolean;
}) {
  const { t } = useTranslation();
  const initial = value == null ? "" : String(value);
  const [draft, setDraft] = useState(initial);
  /* 109 §1d — the DISCREET pattern: the note rests as quiet text and becomes a
     contrasting editable field only on click. A permanently-open textarea read
     as a form; a note is a thought jotted in passing. Blur (and the existing
     Escape / Ctrl+Enter vocabulary) commits and returns it to rest. Plain
     Enter deliberately stays a NEWLINE — a note is multi-line free text, and
     the blur-commit is the same vocabulary the inline editors already use. */
  const [editing, setEditing] = useState(false);
  /* 109 §1d — MEASURED in the drive, a defect this file has carried since 105:
     "Escape reverts" was false. Escape queued `setDraft(initial)` and then
     called `.blur()`, which fires the onBlur commit SYNCHRONOUSLY inside the
     same batch — so commit() read the still-typed draft from this render's
     closure and SAVED the text Escape was discarding. State cannot outrun a
     synchronous native blur; a ref can. */
  const cancelRef = useRef(false);
  const seed = useRef(initial);

  /* The row is live: an SSE push, another window, or the modal can change these
     notes while the drawer is open. Re-seed when the STORED value moves, and
     only then — re-seeding on every render would fight the typist. */
  useEffect(() => {
    if (initial !== seed.current) {
      seed.current = initial;
      setDraft(initial);
    }
  }, [initial]);

  const commit = () => {
    const next = draft.trim();
    if (next === initial.trim()) return;
    seed.current = next;
    onSave(next === "" ? null : next);
  };

  /* 110 §1f — the drawer gets the section heading the other two drawers
     carry (SUBTASKS · DOCUMENT LINKS): without it the note read as a comment
     on whatever sat above it. Every mount of this component IS a drawer
     (censused: CaseTaskRow, CaseEventRow, CaseStageTimeline, Dashboard ×2);
     the modals label their own Notes textareas and never mount this. The
     shell's margin is zeroed so no host shifts.
     111 §4a — one mount proved the exception: the stage row already names
     itself, so CaseStageTimeline passes `hideTitle` and every other mount
     keeps the heading. */
  const heading = (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        /* 216 addendum 7 §11 (2) [RULED 84] — AS SMALL AS « SOUS-TÂCHES »: the
           field label's 9,5 px that addendum 6 gave the subtasks' heading
           (SubtaskChecklist.tsx), once, here — so the three drawers' headings
           read as one size wherever they open (Laurent, 16/10: « same
           treatment to Notes and Documents titles »). */
        fontSize: FS.s9_5,
        fontWeight: 600,
        color: "var(--ts)",
      }}
    >
      {t("notes.section_label", { fallback: "Notes" })}
    </label>
  );

  if (!canWrite || !editing) {
    const shown = initial.trim();
    return (
      <div className="field" style={{ margin: 0 }}>
        {!hideTitle && heading}
      <div
        onClick={
          canWrite
            ? (e) => {
                e.stopPropagation();
                setDraft(initial);
                setEditing(true);
              }
            : undefined
        }
        title={
          canWrite ? t("notes.edit_tip", { fallback: "Click to edit" }) : undefined
        }
        style={{
          fontSize: FS.s11,
          color: shown ? "var(--ts)" : "var(--tm)",
          fontStyle: shown ? undefined : "italic",
          /* The note is free text somebody typed; its line breaks are part of
             what they wrote. Nothing here truncates — that is the point. */
          whiteSpace: "pre-wrap",
          lineHeight: 1.5,
          cursor: canWrite ? "text" : undefined,
        }}
      >
        {shown ||
          (canWrite
            ? t("notes.placeholder", { fallback: "Notes…" })
            : t("notes.empty", { fallback: "No notes." }))}
      </div>
      </div>
    );
  }

  return (
    <div className="field" style={{ margin: 0 }}>
      {!hideTitle && heading}
    <textarea
      {...NO_AUTOFILL}
      className={noAutofillClass("field-input")}
      autoFocus
      value={draft}
      rows={3}
      placeholder={t("notes.placeholder", { fallback: "Notes…" })}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (cancelRef.current) {
          cancelRef.current = false;
          setDraft(initial);
          setEditing(false);
          return;
        }
        commit();
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          cancelRef.current = true;
          (e.target as HTMLTextAreaElement).blur();
        } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
      style={{
        width: "100%",
        margin: 0,
        fontSize: FS.s11,
        lineHeight: 1.5,
        padding: "4px 6px",
        resize: "vertical",
      }}
    />
    </div>
  );
}
